import { Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { RouterLink, RouterOutlet } from '@angular/router';
import { NgScrollbar } from 'ngx-scrollbar';

import { IconDirective } from '@coreui/icons-angular';
import {
  ButtonCloseDirective,
  ContainerComponent,
  INavData,
  ShadowOnScrollDirective,
  SidebarBrandComponent,
  SidebarComponent,
  SidebarFooterComponent,
  SidebarHeaderComponent,
  SidebarNavComponent,
  SidebarToggleDirective,
  SidebarTogglerDirective
} from '@coreui/angular-pro';
import { cilBank } from '@coreui/icons';

import { DefaultAsideComponent, DefaultBreadcrumbComponent, DefaultFooterComponent, DefaultHeaderComponent, SessionTimeoutModalComponent } from '.';
import { navItems as rawNavItems } from './_nav';
import { AuthManager } from '@infra-adapters/services/auth.service';
import { SidebarBadgeService } from '@infra-adapters/services/sidebar-badge.service';
import { IdleWatcherService } from '@infra-adapters/services/idle-watcher.service';
import { AUTO_REFRESH_INTERVAL_MS } from '@utils/auto-refresh';
import { inject } from '@angular/core';

function isOverflown(element: HTMLElement) {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './default-layout.component.html',
  styleUrls: ['./default-layout.component.scss'],
  standalone: true,
  imports: [
    SidebarComponent,
    SidebarHeaderComponent,
    SidebarBrandComponent,
    IconDirective,
    NgScrollbar,
    SidebarNavComponent,
    SidebarToggleDirective,
    SidebarTogglerDirective,
    DefaultAsideComponent,
    DefaultHeaderComponent,
    ShadowOnScrollDirective,
    ContainerComponent,
    RouterOutlet,
    DefaultFooterComponent,
    ButtonCloseDirective,
    DefaultBreadcrumbComponent,
    SessionTimeoutModalComponent
  ]
})
export class DefaultLayoutComponent implements OnInit, OnDestroy {
  private auth = inject(AuthManager);
  private badgeService = inject(SidebarBadgeService);
  private idleWatcher = inject(IdleWatcherService);
  public isAdmin = this.auth.isAdmin;
  public isOperator = this.auth.isOperator;
  // POLAR-702: computed (no un signal plano) para que la sidebar se
  // re-renderice automaticamente ante cualquier cambio en los contadores del
  // badge, sin depender de que algo recuerde llamar a un "rebuild" manual.
  //
  // POLAR-789: se reemplaza el branching estatico Admin/Operador/resto por un
  // filtro generico - todo item con `attributes.permission` se muestra solo si
  // AuthManager.hasPermission() lo confirma (rol u override por usuario); los
  // separadores de seccion (`title: true`, sin permission) siempre se muestran.
  // Admin sigue siendo bypass total (decision explicita del ticket). Ya no hay
  // un navForOperator hardcodeado - cada rol ve exactamente lo que su permiso
  // real habilita, igual que ya hace el guard de rutas.
  public readonly routes = computed<INavData[]>(() => {
    if (this.isAdmin) return this.applyBadges(this.navForAdmin);
    const filtered = this.navForAdmin.filter((item) => {
      const permission = item.attributes?.['permission'];
      if (!permission) return true;
      return this.auth.hasPermission(permission);
    });
    return this.applyBadges(this.removeEmptySectionTitles(filtered));
  });
  public navForAdmin: INavData[] = rawNavItems

  // POLAR-789: el filtro por permiso deja los separadores de seccion (`title:
  // true`) sueltos cuando todos sus items quedan filtrados - un titulo de
  // seccion sin nada debajo. Se recortan aca, despues del filtro.
  private removeEmptySectionTitles(items: INavData[]): INavData[] {
    return items.filter((item, index) => {
      if (!item.title) return true;
      const next = items[index + 1];
      return !!next && !next.title;
    });
  }

  // State: Sidebar notification badges (POLAR-702)
  private refreshHandle?: ReturnType<typeof setInterval>;

  constructor() {
    const { roles } = this.auth.getUser() || { roles: [] };
    console.log(roles);

    this.isAdmin = roles.some(role => role.name === 'Administrator');
    this.isOperator = roles.some(role => role.name === 'Operator');
  }

  async ngOnInit(): Promise<void> {
    await this.badgeService.refreshCounts();
    this.refreshHandle = setInterval(() => {
      this.badgeService.refreshCounts();
    }, AUTO_REFRESH_INTERVAL_MS);
    // POLAR-751: arranca junto con el resto del layout autenticado - cubre
    // todos los roles por igual, ya que este componente solo se monta detras
    // de AuthGuard (nunca en /login).
    this.idleWatcher.start();
  }

  ngOnDestroy(): void {
    if (this.refreshHandle) clearInterval(this.refreshHandle);
    this.idleWatcher.stop();
  }

  private buildBadge(count: number, color: string = 'danger'): { color: string; text: string } | undefined {
    return count > 0 ? { color, text: String(count) } : undefined;
  }

  private applyBadges(navItems: INavData[]): INavData[] {
    return navItems.map(item => {
      if (item.url === '/pages/clients') {
        return { ...item, badge: this.buildBadge(this.badgeService.newClientsCount()) };
      }
      if (item.url === '/pages/orders') {
        return { ...item, badge: this.buildBadge(this.badgeService.pendingOrdersCount()) };
      }
      if (item.url === '/pages/users') {
        return { ...item, badge: this.buildBadge(this.badgeService.totalUsersCount(), 'secondary') };
      }
      return item;
    });
  }

  public icons = {
    cilBank
  };

  onScrollbarUpdate($event: any) {
  }
}
